/**
 * 买分胶囊放在舞台哪个角。纯几何，零 React —— 能在 node 里按四个验收尺寸逐一验。
 *
 * ★ 以前固定在左下角，理由是「左下角盖住也不丢信息」。那是桌面布局的事实：
 *   手机和折叠屏上自己的座位就排在舞台左下（seatGeometry 的 hero 锚点），
 *   360 / 390 / 691 实测胶囊正好压在自己的名字和筹码上 —— 最需要买分的时候，
 *   偏偏看不见自己还剩多少。
 *
 * 做法：按「左下 → 左上（街道标签下面）→ 右下（聊天入口上面）→ 右上」的顺序，
 * 取第一个不压任何座位、自己的底牌、下注筹码、庄家钮、公共牌与底池的角。
 * 胶囊的宽度按最宽的文案估（「等房主批准」），这样文案变了胶囊也不会换角、在屏幕上跳。
 */
import type { Rect, TableLayout } from '../table/seatGeometry.js'

/** 胶囊贴哪两条边、离边多远（舞台坐标，px）。直接当 style 用 */
export interface TopUpSpot {
  readonly corner: 'bottomLeft' | 'topLeft' | 'bottomRight' | 'topRight'
  readonly left?: number
  readonly right?: number
  readonly top?: number
  readonly bottom?: number
}

/** 胶囊最宽的样子：图标 + 「等房主批准」+ 左右内边距（room.css 的 .topup-chip） */
export const TOPUP_CHIP_BOX = { w: 100, h: 36 } as const

/** 离舞台边 12px（--sp-3） */
const EDGE = 12
/** 左上角的街道标签（table.css .table-street：top 8px，约 24px 高）下面再空 4px */
const BELOW_STREET = 36
/** 右下角的聊天入口从操作区探出 28px（--talk-fab-rise），再留 8px */
const ABOVE_TALK_FAB = 28 + 8

/** 状态角标挂在座位块上沿外 10px（table.css .seat__badge），再宽一点点 */
const SEAT_PAD_TOP = 12
const SEAT_PAD = 4
/** 「按住看牌」提示贴在底牌下沿 */
const HERO_HINT_H = 24
/** 下注筹码与庄家钮的大致占位（以锚点为中心） */
const BET_BOX = { w: 60, h: 24 } as const
const DEALER_BOX = 28

interface Box {
  readonly x: number
  readonly y: number
  readonly w: number
  readonly h: number
}

function fromRect(r: Rect, padX = 0, padTop = 0, padBottom = padTop): Box {
  return { x: r.x - padX, y: r.y - padTop, w: r.width + padX * 2, h: r.height + padTop + padBottom }
}

function around(cx: number, cy: number, w: number, h: number): Box {
  return { x: cx - w / 2, y: cy - h / 2, w, h }
}

function overlapArea(a: Box, b: Box): number {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x)
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y)
  return w > 0 && h > 0 ? w * h : 0
}

/** 牌桌上不能被胶囊压住的东西 */
export function topUpObstacles(layout: TableLayout): Box[] {
  const out: Box[] = []
  for (const s of layout.seats) {
    out.push(fromRect(s.box, SEAT_PAD, SEAT_PAD_TOP, SEAT_PAD))
    out.push(around(s.bet.x, s.bet.y, BET_BOX.w, BET_BOX.h))
    out.push(around(s.dealer.x, s.dealer.y, DEALER_BOX, DEALER_BOX))
  }
  if (layout.heroHand !== null) out.push(fromRect(layout.heroHand, 0, 0, HERO_HINT_H))
  out.push(fromRect(layout.board.box))
  out.push(fromRect(layout.pot))
  return out
}

/**
 * 选一个角。
 * @param avoidBottom 单手结算卡开着：它满宽贴底，底下两个角都被它盖着
 */
export function topUpChipSpot(layout: TableLayout, avoidBottom = false): TopUpSpot {
  const W = layout.stage.width
  const H = layout.stage.height
  const { w, h } = TOPUP_CHIP_BOX
  const candidates: readonly { readonly spot: TopUpSpot; readonly box: Box }[] = [
    { spot: { corner: 'bottomLeft', left: EDGE, bottom: EDGE }, box: { x: EDGE, y: H - EDGE - h, w, h } },
    { spot: { corner: 'topLeft', left: EDGE, top: BELOW_STREET }, box: { x: EDGE, y: BELOW_STREET, w, h } },
    {
      spot: { corner: 'bottomRight', right: EDGE, bottom: ABOVE_TALK_FAB },
      box: { x: W - EDGE - w, y: H - ABOVE_TALK_FAB - h, w, h },
    },
    { spot: { corner: 'topRight', right: EDGE, top: EDGE }, box: { x: W - EDGE - w, y: EDGE, w, h } },
  ]
  const usable = avoidBottom ? candidates.filter((c) => c.spot.top !== undefined) : candidates
  const obstacles = topUpObstacles(layout)
  let best: TopUpSpot = SPOT_FALLBACK
  let bestCover = Number.POSITIVE_INFINITY
  for (const c of usable) {
    const cover = obstacles.reduce((sum, o) => sum + overlapArea(c.box, o), 0)
    if (cover === 0) return c.spot
    // 四个角都压着东西（极窄的屏）：退而求其次，压得最少的那个
    if (cover < bestCover) {
      bestCover = cover
      best = c.spot
    }
  }
  return best
}

/** 量不出几何（舞台还没量好）时的默认：左下角，和 room.css 的 .topup-entry 一致 */
export const SPOT_FALLBACK: TopUpSpot = { corner: 'bottomLeft', left: EDGE, bottom: EDGE }
